// fetchTemplateDetails.ts
import { S3_CONFIG } from '../config/apiConfig';
import { getEnvironment } from "../filePathUtils";

// ---- Types ----
export type S3ObjectItem = {
  Key: string;
  Size: number;
  LastModified: string;
  StorageClass: string;
  ETag: string;
  latestVersionOrdinal: string;
  totalVersions: number;
  latestVersionId: string;
};

export type ApiResponse = {
  objects: S3ObjectItem[];
  isTruncated: boolean;
  nextContinuationToken: string | null;
  keyCount: number;
  versionsAugmented: boolean;
};


const S3_KEY_PREFIX = getEnvironment() === 'production'? S3_CONFIG.KEY_File : S3_CONFIG.KEY_File_Dev;

// ---- API URL ----
const API_URL_TEMPLATE = `https://8bjw23y28h.execute-api.eu-west-1.amazonaws.com/prod/s3/list?includeVersions=true&prefix=${encodeURIComponent(S3_KEY_PREFIX)}`;

// ---- Helpers ----
const toISOFromMMDDYYYY = (mmddyyyy: string): string => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(mmddyyyy.trim());
  if (!m) return mmddyyyy;
  const [, MM, DD, YYYY] = m;
  return `${YYYY}-${MM}-${DD}`;
};

const fileNameFromKey = (key: string): string => key.split("/").pop() || "";

const folderFromKey = (key: string): string => {
  const idx = key.lastIndexOf("/");
  return idx === -1 ? "/" : key.substring(0, idx);
};

const extensionFromName = (name: string): string => {
  const idx = name.lastIndexOf(".");
  return idx === -1 ? "" : name.substring(idx + 1).toLowerCase();
};

const encodeKeyForDownload = (key: string): string =>
  encodeURIComponent(key).replace(/%2F/g, "/");

// ---- Transforms ----
export type UiFile = {
  name: string;
  extension: string;
  folder: string;
  size: number;
  originalLastModified: string;
  lastModifiedISO: string;
  etag: string;
  latestVersionId: string;
  latestVersionOrdinal: string;
  downloadPath: string;
  totalVersions: number;
};

export const buildUiFiles = (objects: S3ObjectItem[]): UiFile[] => {
  const withParsedDate = objects.map((o) => ({
    o,
    isoDate: toISOFromMMDDYYYY(o.LastModified),
  }));

  withParsedDate.sort((a, b) =>
    a.isoDate < b.isoDate ? 1 : a.isoDate > b.isoDate ? -1 : 0
  );

  return withParsedDate.map(({ o, isoDate }) => {
    const name = fileNameFromKey(o.Key);
    return {
      name,
      extension: extensionFromName(name),
      folder: folderFromKey(o.Key),
      size: o.Size,
      lastModifiedISO: isoDate,
      etag: o.ETag,
      originalLastModified: o.LastModified,
      latestVersionId: o.latestVersionId,
      latestVersionOrdinal: o.latestVersionOrdinal,
      totalVersions: o.totalVersions,
      downloadPath: `/download/${encodeKeyForDownload(
        o.Key
      )}?versionId=${encodeURIComponent(o.latestVersionId)}`,
    };
  });
};

// ---- Fetcher ----

export async function fetchWithRetry(
  url: string,
  retries = 3,
  delayMs = 1000
): Promise<ApiResponse> {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  } catch (err) {
    if (retries === 0) throw err;
    await new Promise((r) => setTimeout(r, delayMs));
    return fetchWithRetry(url, retries - 1, delayMs);
  }
}


export async function fetchTemplateDetails(
  retries = 3,
  delayMs = 1000
): Promise<UiFile[]> {
    const data = await fetchWithRetry(API_URL_TEMPLATE, retries, delayMs); // ✅ ApiResponse
  return buildUiFiles(data.objects || []);
}

